import { getCookie } from '@/util/url'
import { IGif } from '@giphy/js-types'
import { giphyLocalUrl } from './env'

export type AdEndpointViews =
    | 'xl_carousel_banner'
    | 'homepage_banner' // bouncer banner
    // geo views
    | 'home' // geo home page banner
    | 'gif_details' // gif detail banner
    | 'search_results' // search page banner
    | 'home_grid_0' // geo home grid
    | 'gif_details_grid_0' // geo gif details grid
    | 'search_results_grid_0' // geo search grid

export const gridPathnameMap: Record<string, AdEndpointViews> = {
    '/gifs': 'gif_details_grid_0',
    '/search': 'search_results_grid_0',
    '/': 'home_grid_0',
}

export type AdDecision = {
    gif: IGif
    subdecisions?: AdDecision[]
    cta_text?: string
    cta_url?: string
    impression_url?: string
    click_url?: string
}
export type AdResponse = {
    decisions: Record<string, AdDecision | null>
}

export const hasAnyAd = (adResponse?: AdResponse) => {
    const decisions = adResponse?.decisions || {}
    return Object.keys(decisions).some((key) => {
        return decisions[key] !== null
    })
}
export const getAdDecisionByType = (response: AdResponse | undefined, id?: AdEndpointViews) =>
    id ? (response?.decisions?.[id] as AdDecision) : undefined

export const hasAd = (type: AdEndpointViews, adResponse?: AdResponse) => {
    return !!getAdDecisionByType(adResponse, type)
}

export const mergeResponses = (response1?: AdResponse | undefined, response2?: AdResponse): AdResponse => {
    return {
        decisions: { ...(response1?.decisions || {}), ...(response2?.decisions || {}) },
    }
}

export type AdEndpointRequestOptions = {
    views: AdEndpointViews[]
    rating?: string
    keywords?: string[]
    cookie?: string
    accessToken?: string
    headers?: HeadersInit
}

export type AdEndpointRequestBody = {
    keywords: string[]
    rating: string
    views: AdEndpointViews[]
    platform: string
    testing: boolean
    use_mock: boolean
}

export const fetchAdFromEndpoint = async ({
    views,
    rating = 'unrated',
    keywords = [],
    accessToken = '',
    cookie = '',
    headers: incomingHeaders,
}: AdEndpointRequestOptions): Promise<[AdResponse?, string?]> => {
    const body: AdEndpointRequestBody = {
        keywords,
        rating,
        views,
        platform: 'web',
        testing: getCookie('ads_dev_network', cookie) === 'true',
        use_mock: getCookie('ads_use_mock', cookie) === 'true',
    }
    const fetchUrl = `${giphyLocalUrl}/api/v2/ads/`
    try {
        let headers: HeadersInit = {
            'content-type': 'application/json',
            ...(accessToken ? { authorization: `Token ${accessToken}` } : {}),
            cookie,
            'X-CSRFToken': getCookie('csrftoken', cookie),
        }
        if (incomingHeaders) {
            headers = { ...headers, ...incomingHeaders }
        }
        const response = await fetch(fetchUrl, {
            method: 'POST',
            body: JSON.stringify(body),
            headers,
        })
        // console.log(`ad endpoint request ${fetchUrl}`, JSON.stringify(body))
        if (response.ok) {
            try {
                const result = (await response.json()) as AdResponse

                // For each decision, if click_url and gif exist, set gif.bottle_data?.tdata?.click_out_url = click_url
                Object.values(result?.decisions || {}).forEach((decision) => {
                    if (decision && decision.gif && decision.click_url) {
                        const gif = decision.gif
                        gif.bottle_data = gif.bottle_data ?? {}
                        gif.bottle_data.tdata = gif.bottle_data.tdata ?? {}
                        gif.bottle_data.tdata.click_out_url = decision.click_url
                    }
                })

                // console.log(`ad endpoint result`, result)
                return [result]
            } catch (jsonError) {
                console.error('Failed to parse ad endpoint response as JSON', jsonError)
                return [, `Status:${response.status} ${jsonError}`]
            }
        }
        let text = ''
        try {
            text = await response.text()
        } catch (_e) {}
        return [, `Status:${response.status} ${text}`]
    } catch (e) {
        console.error(e)
        return [, e as any]
    }
}
